import React, { useState, useEffect, useRef } from "react";
import "./css/RecipeAdd.css";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import SEO from "./components/SEO";
import { PROCESS_USER_RECIPE_URL, getRecipeUrl, updateRecipeUrl } from "./config/apiConfig";
import { creators } from "./config/creators";

const STEPS = [
  { id: 1, label: "Details", emoji: "📝" },
  { id: 2, label: "Recipe", emoji: "🥘" },
  { id: 3, label: "Video", emoji: "🎬" },
  { id: 4, label: "Review", emoji: "✨" },
];

const RecipeAdd = () => {
  const navigate = useNavigate();
  const { id: recipeId } = useParams();
  const isEditMode = !!recipeId;

  const [currentStep, setCurrentStep] = useState(1);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [ingredientsText, setIngredientsText] = useState("");
  const [stepsText, setStepsText] = useState("");
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [publishedRecipeId, setPublishedRecipeId] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const thumbnailCleanup = useRef(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("savory_creator");
      if (!raw) {
        navigate("/creators/login");
        return;
      }
      const session = JSON.parse(raw);
      if (!session || !session.email) navigate("/creators/login");
    } catch {
      navigate("/creators/login");
    }
  }, [navigate]);

  useEffect(() => {
    if (isEditMode) {
      fetchRecipe();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeId]);

  useEffect(() => {
    if (thumbnail && thumbnail instanceof File) {
      const url = URL.createObjectURL(thumbnail);
      setThumbnailPreviewUrl(url);
      thumbnailCleanup.current = () => URL.revokeObjectURL(url);
      return () => {
        if (thumbnailCleanup.current) thumbnailCleanup.current();
      };
    } else {
      setThumbnailPreviewUrl(null);
    }
  }, [thumbnail]);

  const fetchRecipe = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(getRecipeUrl(recipeId));
      if (!res.ok) throw new Error("Recipe not found");
      const recipe = await res.json();

      const rawSession = localStorage.getItem("savory_creator");
      if (rawSession) {
        const session = JSON.parse(rawSession);
        const creatorId = session?.creator_id || session?.CreatorId;
        if (creatorId && recipe.content_creator_id !== parseInt(creatorId)) {
          setError("You don't have permission to edit this recipe.");
          setLoading(false);
          return;
        }
      }

      setTitle(recipe.name || "");
      setDescription(recipe.description || "");
      setYoutubeUrl(recipe.youtube_url || "");
      setExistingImageUrl(recipe.url || null);

      if (recipe.ingredients && Array.isArray(recipe.ingredients)) {
        const ingredientsStr = recipe.ingredients
          .map((i) => {
            const parts = [];
            if (i.quantity != null && i.quantity !== "") parts.push(i.quantity);
            if (i.measurement) parts.push(i.measurement);
            // Handle both 'text' and 'description' fields
            const ingredientText = i.text || i.description || "";
            if (ingredientText) parts.push(ingredientText);
            return parts.join(" ");
          })
          .join("\n");
        setIngredientsText(ingredientsStr);
        console.log("📝 Loaded ingredients for editing:", ingredientsStr);
      }

      if (recipe.instructions && Array.isArray(recipe.instructions)) {
        setStepsText(recipe.instructions.join("\n"));
      }

      setLoading(false);
    } catch (err) {
      setError(err.message || "Failed to load recipe");
      setLoading(false);
    }
  };

  const buildIngredientsPayload = () => {
    const t = ingredientsText.trim();
    if (!t) return [];
    // Split by newlines to create separate ingredient entries
    const ingredients = t.split('\n')
      .filter(line => line.trim())
      .map(line => {
        const trimmed = line.trim();
        // Try to parse "quantity measurement ingredient" format
        const match = trimmed.match(/^(\d+\.?\d*)\s+(\w+)\s+(.+)$/);
        if (match) {
          return {
            text: trimmed, // Full line as text (required by API)
            measurement: match[2],
            quantity: parseFloat(match[1])
          };
        }
        // If no match, send as plain text only
        return { text: trimmed };
      });
    console.log("📤 Sending ingredients:", ingredients);
    return ingredients;
  };

  const buildStepsPayload = () => {
    const t = stepsText.trim();
    if (!t) return [];
    // Split by newlines to create separate steps
    return t.split('\n')
      .filter(line => line.trim())
      .map(line => line.trim());
  };

  const isValidYoutubeUrl = (url) => {
    if (!url.trim()) return true;
    const re = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/;
    return re.test(url.trim());
  };

  const goNext = () => {
    setError("");
    if (currentStep === 3 && youtubeUrl.trim() && !isValidYoutubeUrl(youtubeUrl)) {
      setError("Please enter a valid YouTube URL or leave empty.");
      return;
    }
    if (currentStep < 4) setCurrentStep((s) => s + 1);
  };

  const goBack = () => {
    setError("");
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files?.[0] || null;
    setThumbnail(file);
  };

  const handleDeleteRecipe = async (e) => {
    e.preventDefault();
    if (!window.confirm("Delete this recipe? It will be hidden from the feed. You can edit it later to make it visible again.")) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(updateRecipeUrl(recipeId), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ visibility: false }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.message || `Request failed (${res.status})`);
      navigate("/creators/dashboard");
    } catch (err) {
      setError(err.message || "Failed to delete recipe.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!title.trim()) {
      setError("Please enter a recipe title.");
      return;
    }
    if (!description.trim()) {
      setError("Please add a description.");
      return;
    }
    if (!isEditMode && (!thumbnail || !(thumbnail instanceof File))) {
      setError("Please add a photo.");
      return;
    }
    if (!ingredientsText.trim()) {
      setError("Please add ingredients.");
      return;
    }
    if (!stepsText.trim()) {
      setError("Please add steps or instructions.");
      return;
    }

    setSubmitting(true);
    try {
      if (isEditMode) {
        const payload = {
          name: title.trim(),
          description: description.trim(),
          ingredients: buildIngredientsPayload(),
          instructions: buildStepsPayload(),
        };

        if (youtubeUrl.trim() && isValidYoutubeUrl(youtubeUrl)) {
          payload.youtube_url = youtubeUrl.trim();
        } else if (youtubeUrl.trim() === "") {
          payload.youtube_url = null;
        }

        // Include user_id from session if available
        const rawSession = localStorage.getItem("savory_creator");
        if (rawSession) {
          try {
            const session = JSON.parse(rawSession);
            if (session?.userId || session?.user_id) {
              payload.user_id = session.userId || session.user_id;
              console.log("✏️ Updating recipe with user_id:", payload.user_id);
            }
          } catch (_) {}
        }

        console.log("Updating recipe:", recipeId);
        console.log("PATCH URL:", updateRecipeUrl(recipeId));
        console.log("Payload:", payload);

        const res = await fetch(updateRecipeUrl(recipeId), {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        console.log("Response status:", res.status);

        const data = await res.json().catch(() => ({}));
        console.log("Response data:", data);

        if (!res.ok) {
          const msg = data.error || data.message || `Request failed (${res.status})`;
          throw new Error(msg);
        }

        setPublishedRecipeId(recipeId);
        setTimeout(() => {
          navigate(`/recipe-details/${recipeId}`);
        }, 1800);
      } else {
        const formData = new FormData();
        formData.append("title", title.trim());
        formData.append("description", description.trim());
        formData.append("ingredients", JSON.stringify(buildIngredientsPayload()));
        formData.append("steps", JSON.stringify(buildStepsPayload()));
        if (youtubeUrl.trim() && isValidYoutubeUrl(youtubeUrl)) {
          formData.append("youtube_url", youtubeUrl.trim());
        }
        if (thumbnail && thumbnail instanceof File) {
          formData.append("thumbnail", thumbnail);
        }

        const rawSession = localStorage.getItem("savory_creator");
        if (rawSession) {
          try {
            const session = JSON.parse(rawSession);
            
            // Include Supabase user_id if available
            if (session?.userId || session?.user_id) {
              const userId = session.userId || session.user_id;
              formData.append("user_id", userId);
              console.log("📝 Adding recipe with user_id:", userId);
            }
            
            // Include content_creator_id from session (comes from backend)
            const creatorId = session?.creator_id || session?.CreatorId;
            if (creatorId) {
              formData.append("content_creator_id", creatorId);
              console.log("📝 Adding recipe with content_creator_id:", creatorId);
            }
          } catch (_) {}
        }

        const res = await fetch(PROCESS_USER_RECIPE_URL, {
          method: "POST",
          body: formData,
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          const msg = data.error || data.message || `Request failed (${res.status})`;
          throw new Error(msg);
        }

        if (data.success && data.recipeId != null) {
          setPublishedRecipeId(data.recipeId);
          setTimeout(() => {
            navigate(`/recipe-details/${data.recipeId}`);
          }, 1800);
        } else {
          throw new Error(data.message || "Recipe could not be saved.");
        }
      }
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="add-recipe-page add-recipe-fun">
        <div className="add-recipe-loading-screen">
          <div className="add-recipe-loading-spinner" aria-hidden="true" />
          <h2 className="add-recipe-loading-title">Loading recipe…</h2>
        </div>
      </div>
    );
  }

  if (submitting) {
    return (
      <div className="add-recipe-page add-recipe-fun">
        <div className="add-recipe-loading-screen">
          <div className="add-recipe-loading-spinner" aria-hidden="true" />
          <h2 className="add-recipe-loading-title">{isEditMode ? "Updating your recipe…" : "Publishing your recipe…"}</h2>
          <p className="add-recipe-loading-desc">{isEditMode ? "Saving your changes. Hang tight!" : "Our AI is structuring everything. Hang tight!"}</p>
        </div>
      </div>
    );
  }

  if (publishedRecipeId != null) {
    return (
      <div className="add-recipe-page add-recipe-fun">
        <div className="add-recipe-card add-recipe-success-card">
          <div className="add-recipe-success-icon" aria-hidden="true">🎉</div>
          <h2 className="add-recipe-success-title">{isEditMode ? "Recipe updated!" : "Recipe published!"}</h2>
          <p className="add-recipe-success-text">Taking you to your recipe…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="add-recipe-page add-recipe-fun">
      <SEO
        title={`${isEditMode ? "Edit" : "Add"} Recipe | Savory`}
        description={`${isEditMode ? "Edit" : "Create"} a recipe with details, ingredients, video, and more.`}
        keywords="add recipe, create recipe, edit recipe, new recipe"
        url="/recipe-add"
      />

      <header className="add-recipe-topbar">
        <div className="add-recipe-topbar-inner">
          <Link to="/creators/dashboard" className="add-recipe-back">
            <span className="add-recipe-back-arrow">←</span>
            <span>Back</span>
          </Link>
          <h1 className="add-recipe-topbar-title">{isEditMode ? "Edit recipe" : "Add a recipe"}</h1>
        </div>
      </header>

      <div className="add-recipe-stepper-wrap">
        <div className="add-recipe-stepper">
          {STEPS.map((step, i) => (
            <React.Fragment key={step.id}>
              <button
                type="button"
                className={`add-recipe-step-btn ${currentStep === step.id ? "active" : ""} ${currentStep > step.id ? "done" : ""}`}
                onClick={() => currentStep > step.id && setCurrentStep(step.id)}
                disabled={currentStep < step.id}
              >
                <span className="add-recipe-step-num">{step.emoji}</span>
                <span className="add-recipe-step-label">{step.label}</span>
              </button>
              {i < STEPS.length - 1 && <span className="add-recipe-step-connector" />}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="add-recipe-main">
        <div className="add-recipe-card add-recipe-form-card">
          <AnimatePresence mode="wait">
            {currentStep === 1 && (
              <motion.div
                key="step1"
                className="add-recipe-step-content"
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.25 }}
              >
                <h2 className="add-recipe-step-heading">📝 Details</h2>
                <p className="add-recipe-step-desc">Give your recipe a name, a short description, and a photo.</p>
                <div className="add-recipe-fields">
                  <div className="add-recipe-field">
                    <label htmlFor="recipe-title" className="add-recipe-label">Title</label>
                    <input
                      id="recipe-title"
                      type="text"
                      className="add-recipe-input"
                      placeholder="e.g. My Famous Chocolate Chip Cookies"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  <div className="add-recipe-field">
                    <label htmlFor="recipe-description" className="add-recipe-label">Description</label>
                    <textarea
                      id="recipe-description"
                      className="add-recipe-textarea"
                      placeholder="Short description of your recipe."
                      rows={4}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </div>
                  <div className="add-recipe-field">
                    <label htmlFor="recipe-thumbnail" className="add-recipe-label">Photo {isEditMode && <span className="add-recipe-optional">(leave empty to keep current)</span>}</label>
                    <input
                      id="recipe-thumbnail"
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="add-recipe-file"
                      onChange={handleThumbnailChange}
                      aria-label="Recipe photo"
                    />
                    {thumbnailPreviewUrl && (
                      <div className="add-recipe-thumbnail-preview">
                        <img src={thumbnailPreviewUrl} alt="Recipe preview" />
                        <span className="add-recipe-thumbnail-name">{thumbnail?.name}</span>
                      </div>
                    )}
                    {!thumbnailPreviewUrl && existingImageUrl && (
                      <div className="add-recipe-thumbnail-preview">
                        <img src={existingImageUrl} alt="Current recipe" />
                        <span className="add-recipe-thumbnail-name">Current photo</span>
                      </div>
                    )}
                  </div>
                </div>
                {error && <div className="add-recipe-error" role="alert">{error}</div>}
                <div className="add-recipe-actions">
                  <button type="button" className="add-recipe-btn-secondary" onClick={goNext}>
                    Next →
                  </button>
                </div>
              </motion.div>
            )}

            {currentStep === 2 && (
              <motion.div
                key="step2"
                className="add-recipe-step-content"
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.25 }}
              >
                <h2 className="add-recipe-step-heading">🥘 Recipe</h2>
                <p className="add-recipe-step-desc">Write ingredients and steps in your own words.</p>
                {!isEditMode && (
                  <div className="add-recipe-ai-note">
                    <span className="add-recipe-ai-note-icon">✨</span>
                    Our AI will look over your recipe and make it clearer and better formatted — ingredients, steps, and nutrition. Just write it your way and we'll polish it.
                  </div>
                )}
                <div className="add-recipe-field">
                  <label htmlFor="recipe-ingredients" className="add-recipe-label">Ingredients</label>
                  <textarea
                    id="recipe-ingredients"
                    className="add-recipe-textarea add-recipe-textarea-large"
                    placeholder={"e.g. 2 chicken breasts, 1 cup rice, salt and pepper\nor just list everything you use"}
                    rows={5}
                    value={ingredientsText}
                    onChange={(e) => setIngredientsText(e.target.value)}
                    aria-label="Ingredients"
                  />
                </div>
                <div className="add-recipe-field">
                  <label htmlFor="recipe-steps" className="add-recipe-label">Steps</label>
                  <textarea
                    id="recipe-steps"
                    className="add-recipe-textarea add-recipe-textarea-large"
                    placeholder={"e.g. Cook the chicken. Add rice. Simmer 20 min. Serve.\nor describe it your way"}
                    rows={5}
                    value={stepsText}
                    onChange={(e) => setStepsText(e.target.value)}
                    aria-label="Steps"
                  />
                </div>
                {error && <div className="add-recipe-error" role="alert">{error}</div>}
                <div className="add-recipe-actions">
                  <button type="button" className="add-recipe-btn-secondary" onClick={goBack}>
                    ← Back
                  </button>
                  <button type="button" className="add-recipe-btn-primary" onClick={goNext}>
                    Next →
                  </button>
                </div>
              </motion.div>
            )}

            {currentStep === 3 && (
              <motion.div
                key="step3"
                className="add-recipe-step-content"
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.25 }}
              >
                <h2 className="add-recipe-step-heading">🎬 Video <span className="add-recipe-optional-badge">(optional)</span></h2>
                <p className="add-recipe-step-desc">Adding a YouTube link is optional. If you have a video, paste it here — otherwise skip this step. Our AI will still review and improve your recipe either way.</p>
                <div className="add-recipe-video-zone">
                  <span className="add-recipe-video-emoji" aria-hidden="true">▶️</span>
                  <input
                    type="url"
                    className="add-recipe-video-input"
                    placeholder="Paste YouTube URL here (optional)"
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    aria-label="YouTube video URL"
                  />
                </div>
                {error && <div className="add-recipe-error" role="alert">{error}</div>}
                <div className="add-recipe-actions">
                  <button type="button" className="add-recipe-btn-secondary" onClick={goBack}>
                    ← Back
                  </button>
                  <button type="button" className="add-recipe-btn-primary" onClick={goNext}>
                    Review →
                  </button>
                </div>
              </motion.div>
            )}

            {currentStep === 4 && (
              <motion.form
                key="step4"
                className="add-recipe-step-content add-recipe-review"
                onSubmit={handleSubmit}
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.25 }}
              >
                <h2 className="add-recipe-step-heading">✨ Review</h2>
                <p className="add-recipe-step-desc">{isEditMode ? "Here's what you updated. Check everything, then save your changes." : "Here's what you entered. Our AI will look over it and make it look better before publishing. Check everything, then hit Publish."}</p>

                {!isEditMode && (
                  <div className="add-recipe-review-intro">
                    <span className="add-recipe-review-intro-icon">🤖</span>
                    After you publish, our AI will clean up your recipe — clearer formatting, better structure, and nutrition info.
                  </div>
                )}

                <div className="add-recipe-review-summary">
                  <div className="add-recipe-review-row add-recipe-review-header-row">
                    {(thumbnailPreviewUrl || existingImageUrl) && (
                      <div className="add-recipe-review-photo-wrap">
                        <img src={thumbnailPreviewUrl || existingImageUrl} alt="Recipe" />
                      </div>
                    )}
                    <div className="add-recipe-review-title-wrap">
                      <span className="add-recipe-review-section-label">Recipe title</span>
                      <h3 className="add-recipe-review-title">{title || "—"}</h3>
                      {description.trim() && (
                        <div className="add-recipe-review-meta">
                          <p className="add-recipe-review-desc">{description}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="add-recipe-review-section">
                    <span className="add-recipe-review-section-label">Ingredients</span>
                    <div className="add-recipe-review-box">
                      <p className="add-recipe-review-body">{ingredientsText || "—"}</p>
                    </div>
                  </div>

                  <div className="add-recipe-review-section">
                    <span className="add-recipe-review-section-label">Steps</span>
                    <div className="add-recipe-review-box">
                      <p className="add-recipe-review-body">{stepsText || "—"}</p>
                    </div>
                  </div>

                  {youtubeUrl.trim() && (
                    <div className="add-recipe-review-section">
                      <span className="add-recipe-review-section-label">Video link (optional)</span>
                      <div className="add-recipe-review-box add-recipe-review-box-link">
                        <p className="add-recipe-review-body add-recipe-review-link">{youtubeUrl}</p>
                      </div>
                    </div>
                  )}
                </div>

                {error && <div className="add-recipe-error" role="alert">{error}</div>}
                <div className="add-recipe-actions add-recipe-review-actions">
                  {isEditMode && (
                    <button
                      type="button"
                      className="add-recipe-btn-delete"
                      onClick={handleDeleteRecipe}
                      disabled={submitting}
                    >
                      Delete recipe
                    </button>
                  )}
                  <button type="button" className="add-recipe-btn-secondary" onClick={goBack}>
                    ← Back
                  </button>
                  <button type="submit" className="add-recipe-btn-primary add-recipe-btn-publish" disabled={submitting}>
                    {isEditMode ? "Save changes 💾" : "Publish recipe 🚀"}
                  </button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default RecipeAdd;
